import { ZoomPhoneIntegration } from "@/schema/zoom-phone-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const ZOOM_PHONE_INTEGRATIONS_QUERY_KEY = ["zoom-phone-integrations"]

export function useFetchZoomPhoneIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<ZoomPhoneIntegration[]>({
    queryKey: [...ZOOM_PHONE_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const data = await apiContextFetch<ZoomPhoneIntegration[]>(context, "zoom_phone_integrations")
      return data
    },
    enabled,
    refetchInterval: (query) =>
      query.state.data?.some(
        (integration) =>
          integration.configured &&
          integration.connected &&
          !integration.needs_reauth &&
          integration.calling_phone_numbers === null
      )
        ? 3000
        : false,
  })
}

export function useGetZoomPhoneAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string; integrationGuid?: string } | void>({
    mutationKey: ["zoom-phone-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) {
        searchParams.append("return_url", params.returnUrl)
      }
      if (params && "integrationGuid" in params && params.integrationGuid) {
        searchParams.append("integration_guid", params.integrationGuid)
      }

      const url = `zoom_phone_integrations/get_auth_uri${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      return apiContextFetch<AuthUriResponse>(context, url)
    },
  })
}

export function useDisconnectZoomPhoneIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["zoom-phone-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `zoom_phone_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ZOOM_PHONE_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDeleteZoomPhoneIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `zoom_phone_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ZOOM_PHONE_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
